import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import DOMPurify from "dompurify";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"; // Ensure default toolbar styles load
import styles from "./CompanyCompanyAssessmentStyles.module.css"; // Your CSS module
import Wizard from "../../CompanyDashboard/CompanyContent/Wizard";

const CompanyAssessment = () => {
  // Extract company code (id) and department assignment id (deptid) from URL
  const { id, deptid } = useParams();
  const companycode = id;
  const companydeptid = deptid;
  const companyName = localStorage.getItem("clickedCompanyName") || "No Company";
  const imageFiles = localStorage.getItem("clickedImageFile") || "";
  const imageUrl = imageFiles ? `https://starbehaviors.com/uploadimage/${imageFiles}` : "";
  // Local state matching API body fields
  const [company_department, setCompanyDepartment] = useState("Fetching department...");
  const [admin_site_url, setAdminSiteUrl] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [adminemails, setAdminemails] = useState("");
  const [adminpageheader, setAdminpageheader] = useState("");
  const [adminpagecontent, setAdminpagecontent] = useState("");
  const [adminpageoffers, setAdminpageoffers] = useState("");
  const [adminpagecta, setAdminpagecta] = useState("");
  const [assessmentRecipient, setAssessmentRecipient] = useState("");

  // For handling multiple recipients and offers as arrays
  const [recipientInput, setRecipientInput] = useState("");
  const [recipientsArray, setRecipientsArray] = useState([]);
  const [offerInput, setOfferInput] = useState("");
  const [offersArray, setOffersArray] = useState([]);

  // Draft.js editor state
  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(ContentState.createFromText(""))
  );

  // UI states for form validation and messaging
  const [formErrors, setFormErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // 1) Fetch the department name from the master list using deptid
  const fetchDepartmentName = async () => {
    try {
      const resp = await axios.post("https://casaahaanahotels.com/get_master_assessment_type", {});
      if (Array.isArray(resp.data)) {
        const found = resp.data.find((dept) => dept.assessment_type_id === deptid);
        if (found) {
          setCompanyDepartment(found.AssessmentType);
        } else {
          setCompanyDepartment("Unknown Department");
        }
      } else {
        setCompanyDepartment("Unknown Department");
      }
    } catch (error) {
      console.error("Error fetching department:", error);
      setCompanyDepartment("Unknown Department");
    }
  };

  useEffect(() => {
    fetchDepartmentName();
  }, [deptid]);

  // 2) Handle file change for image upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setFormErrors((prev) => ({
        ...prev,
        imageFile: "Image size should be less than 1MB",
      }));
      return;
    }

    setImageFile(file.name);

    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(
        "https://starbehaviors.com/uploadimage.php",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("Image uploaded successfully:", response.data);
    } catch (error) {
      console.error("Image upload failed:", error);
      setFormErrors((prev) => ({
        ...prev,
        imageFile: "Image upload failed",
      }));
    }
  };

  // 3) Update editor state and capture HTML content
  const handleEditorStateChange = (newState) => {
    setEditorState(newState);
    const rawContent = convertToRaw(newState.getCurrentContent());
    const htmlContent = draftToHtml(rawContent);
    const cleanedHtml = DOMPurify.sanitize(htmlContent);
    setAdminpagecontent(cleanedHtml);
  };

  // 4) Recipients handling
  const addRecipient = () => {
    if (!recipientInput.trim()) {
      alert("Please enter an email address first.");
      return;
    }
    setRecipientsArray([...recipientsArray, recipientInput.trim()]);
    setRecipientInput("");
  };

  const removeRecipient = (index) => {
    const updated = [...recipientsArray];
    updated.splice(index, 1);
    setRecipientsArray(updated);
  };

  // 5) Offers handling
  const addOffer = () => {
    if (!offerInput.trim()) {
      alert("Please enter an offer text first.");
      return;
    }
    setOffersArray([...offersArray, offerInput.trim()]);
    setOfferInput("");
  };

  const removeOffer = (index) => {
    const updated = [...offersArray];
    updated.splice(index, 1);
    setOffersArray(updated);
  };

  // 6) Basic validation functions
  const isValidEmail = (email) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return regex.test(email);
  };

  const isValidUrl = (url) => {
    const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
    return urlRegex.test(url);
  };

  const validateForm = () => {
    let errors = {};

    if (recipientsArray.length === 0) {
      errors.assessmentRecipient = "At least one recipient is required";
    } else {
      for (let i = 0; i < recipientsArray.length; i++) {
        if (!isValidEmail(recipientsArray[i])) {
          errors.assessmentRecipient = `Invalid email format: ${recipientsArray[i]}`;
          break;
        }
      }
    }

    if (!admin_site_url.trim()) {
      errors.admin_site_url = "Admin Site URL is required";
    } else if (!isValidUrl(admin_site_url.trim())) {
      errors.admin_site_url = "Invalid URL format";
    }

    if (!adminpageheader.trim()) {
      errors.adminpageheader = "Header is required";
    }

    if (!adminpagecontent.trim()) {
      errors.adminpagecontent = "Content is required";
    }

    if (offersArray.length === 0) {
      errors.adminpageoffers = "At least one offer is required";
    }

    if (!adminpagecta.trim()) {
      errors.adminpagecta = "Call to Action is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 7) Handle Save: build comma-separated strings and post data to API
  const handleSave = async () => {
    const recipientsString = recipientsArray.join(",");
    const offersString = offersArray.join(",");

    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const dataToSend = {
      companydeptid, // from URL
      companycode,   // from URL
      company_department, // fetched department name
      admin_site_url,
      imageFile: imageFile || "",
      adminemails,
      adminpageheader,
      adminpagecontent,
      adminpageoffers: offersString,
      adminpagecta,
      assessmentRecipient: recipientsString,
    };

    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/sb_addcompanydepartment",
        dataToSend,
        { headers: { "Content-Type": "application/json" } }
      );
      console.log("Server Response:", response.data);
      if (response.data && response.data.Message) {
        if (response.data.Message === "Department created successfully") {
          setSuccessMessage("Department created successfully");
          setTimeout(() => {
            clearForm();
          }, 2000);
        } else if (response.data.Message === "Duplicate Department  Exist") {
          setErrorMessage("Duplicate Department Exists. Please use another department.");
        } else {
          setErrorMessage("Failed to create department. " + response.data.Message);
        }
      } else {
        setErrorMessage("Failed to create department. Unknown error.");
      }
    } catch (error) {
      console.error("Error saving:", error);
      setErrorMessage("An error occurred while saving the form.");
    }
    setLoading(false);
  };

  // 8) Clear form fields
  const clearForm = () => {
    setCompanyDepartment("Unknown Department");
    setAdminSiteUrl("");
    setImageFile("");
    setEditorState(EditorState.createWithContent(ContentState.createFromText("")));
    setAdminemails("");
    setAdminpageheader("");
    setAdminpagecontent("");
    setAdminpageoffers("");
    setAdminpagecta("");
    setAssessmentRecipient("");
    setRecipientInput("");
    setRecipientsArray([]);
    setOfferInput("");
    setOffersArray([]);
    setFormErrors({});
    setErrorMessage("");
    setSuccessMessage("");
    setLoading(false);
  };

  return (
    <div className={styles.formContainer}>
     <Wizard currentStep={3} />
    <h1 className={styles.companyPageTitle}>{companyName}</h1>
      {imageUrl && (
        <div className={styles.imageWrapper}>
          <img src={imageUrl} alt={companyName} className={styles.companyImage} />
        </div>
      )}

      <hr className={styles.sectionDivider} />
      <h2>Company Assessment</h2>
      {/* {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>} */}

      <form id="companyAssessmentForm" className={styles.assignForm}>
        {/* Department (read-only) */}
        <div className={styles.formRow}>
          <label>Department</label>
          <input
            type="text"
            value={company_department}
            readOnly
            className={styles.selectField}
          />
        </div>

      

        {/* Admin Site URL */}
        <div className={styles.formRow}>
          <label>Admin Site URL*</label>
          <input
            className={styles.selectField}
            placeholder="https://admin.example.com"
            value={admin_site_url}
            onChange={(e) => setAdminSiteUrl(e.target.value)}
          />
          {formErrors.admin_site_url && (
            <div className={styles.errorMessage}>{formErrors.admin_site_url}</div>
          )}
        </div>

        {/* Landing Page Header */}
        <div className={styles.formRow}>
          <label>Landing Page Header*</label>
          <input
            className={styles.selectField}
            placeholder="Welcome to the Admin Panel"
            value={adminpageheader}
            onChange={(e) => setAdminpageheader(e.target.value)}
          />
          {formErrors.adminpageheader && (
            <div className={styles.errorMessage}>{formErrors.adminpageheader}</div>
          )}
        </div>

      

      

        {/* Landing Page Call to Action */}
        <div className={styles.formRow}>
          <label>Landing Page Call to Action*</label>
          <input
            className={styles.selectField}
            placeholder="Upgrade Now"
            value={adminpagecta}
            onChange={(e) => setAdminpagecta(e.target.value)}
          />
          {formErrors.adminpagecta && (
            <div className={styles.errorMessage}>{formErrors.adminpagecta}</div>
          )}
        </div>

        {/* Upload Image */}
        <div className={styles.formRow}>
          <label>Upload Image*</label>
          <input
            type="file"
            onChange={handleFileChange}
            className={formErrors.imageFile ? styles.errorInput : styles.selectField}
          />
          {formErrors.imageFile && (
            <div className={styles.errorMessage}>{formErrors.imageFile}</div>
          )}
        </div>

        {/* Admin Emails (Optional) */}
        <div className={styles.formRow}>
          <label>Admin Emails (Optional)</label>
          <input
            className={styles.selectField}
            placeholder="admin@example.com, admin2@example.com"
            value={adminemails}
            onChange={(e) => setAdminemails(e.target.value)}
          />
        </div>
    
          {/* Landing Page Main Content (Draft.js) */}
       
          {/* Recipients (multiple) */}
          <div className={styles.formRow}>
          <label>Assessment Recipient*</label>
          <div style={{ display: "flex", gap: "8px", width: "100%" }}>
            <input
              className={styles.selectField}
              placeholder="name@example.com"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
            />
            <button
              type="button"
              className={styles.submitButton}
              style={{ width: "120px" }}
              onClick={addRecipient}
            >
              Add
            </button>
          </div>
          {formErrors.assessmentRecipient && (
            <div className={styles.errorMessage}>{formErrors.assessmentRecipient}</div>
          )}
        </div>

        {/* Display Recipients */}
        {recipientsArray.length > 0 && (
          <div className={styles.formRow}>
            <label>Recipients:</label>
            <ul style={{ listStyle: "none", paddingLeft: 0 }}>
              {recipientsArray.map((r, i) => (
                <li key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{r}</span>
                  <button
                    type="button"
                    className={styles.submitButton}
                    style={{ backgroundColor: "#dc3545", width: "30px",margin:"2px" }}
                    onClick={() => removeRecipient(i)}
                  >
                    X
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
              {/* Landing Page Offers (multiple) */}
              <div className={styles.formRow}>
          <label>Landing Page Offers*</label>
          <div style={{ display: "flex", gap: "8px", width: "100%" }}>
            <input
              className={styles.selectField}
              placeholder="Add an offer..."
              value={offerInput}
              onChange={(e) => setOfferInput(e.target.value)}
            />
            <button
              type="button"
              className={styles.submitButton}
              style={{ width: "120px" }}
              onClick={addOffer}
            >
              Add
            </button>
          </div>
          {formErrors.adminpageoffers && (
            <div className={styles.errorMessage}>{formErrors.adminpageoffers}</div>
          )}
        </div>
        {offersArray.length > 0 && (
          <div className={styles.formRow}>
            <label>Offers:</label>
            <ul style={{ listStyle: "none", paddingLeft: 0 }}>
              {offersArray.map((offer, idx) => (
                <li key={idx} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{offer}</span>
                  <button
                    type="button"
                    className={styles.submitButton}
                    style={{ backgroundColor: "#dc3545", width: "30px",margin:"2px" }}
                    onClick={() => removeOffer(idx)}
                  >
                    X
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className={styles.editor}>
          <label>Landing Page Main Content*</label>
          <div className={styles.editorWrapper}>
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorStateChange}
              wrapperClassName={styles.editorWrapper}
              editorClassName={styles.editorContent}
              toolbarClassName={styles.editorToolbar}
              toolbar={{
                inline: { inDropdown: true },
                list: { inDropdown: true },
                textAlign: { inDropdown: true },
                link: { inDropdown: true },
                history: { inDropdown: true },
                image: {
                  uploadCallback: (file) =>
                    new Promise((resolve) => {
                      resolve({ data: { link: "https://example.com/dummy.png" } });
                    }),
                  alt: { present: true, mandatory: true },
                },
              }}
            />
          </div>
          {formErrors.adminpagecontent && (
            <div className={styles.errorMessage}>{formErrors.adminpagecontent}</div>
          )}
        </div>
     

        {/* Action Buttons */}
        <div className={styles.buttonContainer}>
          <button
            type="button"
            onClick={handleSave}
            className={styles.submitButton}
          >
            {loading ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={clearForm}
            className={styles.submitButton}
            style={{ marginLeft: "10px", backgroundColor: "red" }}
          >
            Clear
          </button>
        </div>
        {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
        {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      </form>
      
    </div>
  );
};

export default CompanyAssessment;
